import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EventNote as CalendarIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { scheduleService, projectService } from '../services/api';

interface Schedule {
  id: number;
  project_id: number;
  project?: {
    id: number;
    name: string;
  };
  name: string;
  frequency: 'daily' | 'weekly' | 'custom';
  days_of_week?: number[];
  times: string[];
  platforms: string[];
  is_active: boolean;
  next_run?: string;
  last_run?: string;
  total_runs: number;
  created_at: string;
}

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
];

const Schedules: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [upcomingPosts, setUpcomingPosts] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [scheduleToDelete, setScheduleToDelete] = useState<number | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    project_id: '',
    name: '',
    frequency: 'daily' as 'daily' | 'weekly' | 'custom',
    days_of_week: [] as number[],
    times: ['09:00'] as string[],
    platforms: [] as string[],
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch projects
      const projectsResponse = await projectService.list();
      const projectList = projectsResponse.data?.projects || projectsResponse.data || [];
      setProjects(Array.isArray(projectList) ? projectList : []);

      // Fetch schedules for each project
      const allSchedules: Schedule[] = [];
      for (const project of (Array.isArray(projectList) ? projectList : [])) {
        try {
          const schedResponse = await scheduleService.listByProject(project.id);
          const schedData = schedResponse.data?.schedules || [];
          for (const s of schedData) {
            allSchedules.push({
              ...s,
              project: { id: project.id, name: project.name },
              times: s.times || JSON.parse(s.times_json || '[]'),
              platforms: s.platforms || JSON.parse(s.platforms_json || '[]'),
              days_of_week: s.days_of_week || (s.days_of_week_json ? JSON.parse(s.days_of_week_json) : undefined),
              next_run: s.next_run_at,
              last_run: s.last_run_at,
            });
          }
        } catch {
          // Skip failed project
        }
      }
      setSchedules(allSchedules);

      // Fetch upcoming posts
      try {
        const upcomingResponse = await scheduleService.upcoming(7);
        const upcomingData = upcomingResponse.data?.upcoming || upcomingResponse.data?.schedules || [];
        setUpcomingPosts(Array.isArray(upcomingData) ? upcomingData : []);
      } catch {
        setUpcomingPosts([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = () => {
    setSelectedSchedule(null);
    setFormData({
      project_id: '',
      name: '',
      frequency: 'daily',
      days_of_week: [],
      times: ['09:00'],
      platforms: [],
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      project_id: schedule.project_id.toString(),
      name: schedule.name,
      frequency: schedule.frequency,
      days_of_week: schedule.days_of_week || [],
      times: schedule.times,
      platforms: schedule.platforms,
      is_active: schedule.is_active,
    });
    setDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (!formData.project_id || !formData.name || formData.platforms.length === 0) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
      return;
    }
    
    try {
      setLoading(true);
      if (selectedSchedule) {
        // Update existing schedule
        await scheduleService.update(selectedSchedule.id, formData);
        enqueueSnackbar('Schedule updated successfully', { variant: 'success' });
      } else {
        // Create new schedule
        await scheduleService.create(formData);
        enqueueSnackbar('Schedule created successfully', { variant: 'success' });
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save schedule:', error);
      enqueueSnackbar('Failed to save schedule', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSchedule = async (id: number) => {
    try {
      await scheduleService.toggle(id);
      enqueueSnackbar('Schedule toggled successfully', { variant: 'success' });
      fetchData();
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
      enqueueSnackbar('Failed to toggle schedule', { variant: 'error' });
    }
  };

  const handleDeleteClick = (id: number) => {
    setScheduleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!scheduleToDelete) return;
    
    try {
      setLoading(true);
      await scheduleService.delete(scheduleToDelete);
      enqueueSnackbar('Schedule deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      enqueueSnackbar('Failed to delete schedule', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTime = () => {
    setFormData({
      ...formData,
      times: [...formData.times, '09:00'],
    });
  };

  const handleRemoveTime = (index: number) => {
    setFormData({
      ...formData,
      times: formData.times.filter((_, i) => i !== index),
    });
  };

  const getFrequencyDisplay = (schedule: Schedule) => {
    switch (schedule.frequency) {
      case 'daily':
        return 'Every day';
      case 'weekly':
        return `Every ${schedule.days_of_week?.map(d => 
          DAYS_OF_WEEK.find(day => day.value === d)?.label
        ).join(', ')}`;
      case 'custom':
        return 'Custom schedule';
      default:
        return schedule.frequency;
    }
  };

  if (loading && schedules.length === 0) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Automation Schedules
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSchedule}
            >
              Create Schedule
            </Button>
          </Box>
        </Box>

        {/* Active Schedules */}
        <Typography variant="h6" gutterBottom>
          Active Schedules
        </Typography>
        <Grid container spacing={3} mb={4}>
          {schedules.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No schedules created yet
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Create automation schedules to post your reels automatically
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateSchedule}
                >
                  Create First Schedule
                </Button>
              </Paper>
            </Grid>
          ) : (
            schedules && Array.isArray(schedules) ? schedules.map((schedule) => (
              <Grid item xs={12} md={6} key={schedule.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="h6" gutterBottom>
                          {schedule.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {schedule.project?.name}
                        </Typography>
                        
                        <Box my={2}>
                          <Chip
                            icon={<CalendarIcon />}
                            label={getFrequencyDisplay(schedule)}
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                          />
                          {schedule.times.map((time) => (
                            <Chip
                              key={time}
                              icon={<TimerIcon />}
                              label={time}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          ))}
                        </Box>
                        
                        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                          {schedule.platforms.map((platform) => (
                            <Chip
                              key={platform}
                              label={platform}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                        
                        {schedule.next_run && (
                          <Typography variant="body2" color="text.secondary">
                            Next run: {format(new Date(schedule.next_run), 'MMM d, h:mm a')}
                          </Typography>
                        )}
                        
                        <Typography variant="body2" color="text.secondary">
                          Total runs: {schedule.total_runs}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Tooltip title={schedule.is_active ? 'Pause' : 'Resume'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleSchedule(schedule.id)}
                            color={schedule.is_active ? 'primary' : 'default'}
                          >
                            {schedule.is_active ? <PauseIcon /> : <PlayIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditSchedule(schedule)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(schedule.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No schedules available
                </Typography>
              </Grid>
            )
          )}
        </Grid>

        {/* Upcoming Posts */}
        <Typography variant="h6" gutterBottom>
          Upcoming Posts (Next 7 Days)
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Schedule</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Platforms</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {upcomingPosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" py={3}>
                      No upcoming posts scheduled
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                upcomingPosts.map((post) => (
                  <TableRow key={post.id}>
                    <TableCell>{post.schedule_name}</TableCell>
                    <TableCell>{post.project_name}</TableCell>
                    <TableCell>
                      {format(new Date(post.scheduled_time), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        {post.platforms.map((platform: string) => (
                          <Chip key={platform} label={platform} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create/Edit Schedule Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSchedule ? 'Edit Schedule' : 'Create Schedule'}
        </DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Project</InputLabel>
                  <Select
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    label="Project"
                  >
                    {projects && Array.isArray(projects) ? projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.name}
                      </MenuItem>
                    )) : null}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Schedule Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
                    label="Frequency"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {formData.frequency !== 'daily' && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Days of Week
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {DAYS_OF_WEEK.map((day) => (
                      <Chip
                        key={day.value}
                        label={day.label}
                        onClick={() => {
                          const newDays = formData.days_of_week.includes(day.value)
                            ? formData.days_of_week.filter(d => d !== day.value)
                            : [...formData.days_of_week, day.value];
                          setFormData({ ...formData, days_of_week: newDays });
                        }}
                        color={formData.days_of_week.includes(day.value) ? 'primary' : 'default'}
                        variant={formData.days_of_week.includes(day.value) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Posting Times
                </Typography>
                {formData.times.map((time, index) => (
                  <Box key={index} display="flex" gap={1} mb={1}>
                    <TextField
                      type="time"
                      value={time}
                      onChange={(e) => {
                        const newTimes = [...formData.times];
                        newTimes[index] = e.target.value;
                        setFormData({ ...formData, times: newTimes });
                      }}
                      fullWidth
                    />
                    {formData.times.length > 1 && (
                      <IconButton onClick={() => handleRemoveTime(index)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddTime}
                >
                  Add Time
                </Button>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Platforms
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {PLATFORMS.map((platform) => (
                    <Chip
                      key={platform.value}
                      label={platform.label}
                      onClick={() => {
                        const newPlatforms = formData.platforms.includes(platform.value)
                          ? formData.platforms.filter(p => p !== platform.value)
                          : [...formData.platforms, platform.value];
                        setFormData({ ...formData, platforms: newPlatforms });
                      }}
                      color={formData.platforms.includes(platform.value) ? 'primary' : 'default'}
                      variant={formData.platforms.includes(platform.value) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSchedule} variant="contained" disabled={loading}>
            {selectedSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Schedule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this schedule? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Schedules;